import React from 'react';
import { GEOMET_WMS_URL, RADAR_LAYERS } from './radarLayers';
import type { RadarLayerKey } from './radarLayers';

interface MapLegendProps {
  activeOverlay: string;
  activeRadarLayer?: RadarLayerKey;
}

const CBH_LEGEND = [
  { color: '#1e3a5f', label: '< 500m', sub: 'Plafond très bas' },
  { color: '#1a5276', label: '500–1000m', sub: 'Bas' },
  { color: '#1f618d', label: '1000–1500m', sub: 'Modéré' },
  { color: '#2874a6', label: '1500–2000m', sub: 'Bon' },
  { color: '#2e86c1', label: '2000–2500m', sub: 'Très bon' },
  { color: '#00d4ff', label: '> 2500m', sub: 'Excellent' },
];

const WIND_LEGEND = [
  { color: '#34d399', label: '< 15kt', sub: 'Calme' },
  { color: '#fbbf24', label: '15–25kt', sub: 'Modéré' },
  { color: '#f87171', label: '25–40kt', sub: 'Fort' },
  { color: '#a78bfa', label: '> 40kt', sub: 'Très fort' },
];

const STORM_LEGEND = [
  { color: 'rgba(251,191,36,0.4)', label: '< 30%', sub: 'Faible risque' },
  { color: 'rgba(248,113,113,0.5)', label: '30–60%', sub: 'Risque modéré' },
  { color: 'rgba(167,139,250,0.6)', label: '> 60%', sub: 'Risque élevé' },
];

const PRESSURE_LEGEND = [
  { color: '#f8fafc', label: 'Isobares', sub: 'lignes hPa indicatives' },
  { color: '#a78bfa', label: 'Baisse', sub: 'instabilite possible' },
  { color: '#34d399', label: 'Hausse', sub: 'air plus stable' },
];

export default function MapLegend({ activeOverlay, activeRadarLayer = 'rain' }: MapLegendProps) {
  const radarLayer = RADAR_LAYERS[activeRadarLayer];
  const radarLegendUrl = `${GEOMET_WMS_URL}?version=1.3.0&service=WMS&request=GetLegendGraphic&sld_version=1.1.0&layer=${encodeURIComponent(radarLayer.legendLayer)}&format=image/png${radarLayer.legendStyle ? `&STYLE=${encodeURIComponent(radarLayer.legendStyle)}` : ''}`;
  const items =
    activeOverlay === 'cbh' ? CBH_LEGEND :
    activeOverlay === 'wind' ? WIND_LEGEND :
    activeOverlay === 'pressure' ? PRESSURE_LEGEND :
    STORM_LEGEND;

  const title =
    activeOverlay === 'cbh' ? 'Base des Cumulus' :
    activeOverlay === 'wind'? 'Vitesse du Vent' :
    activeOverlay === 'radar' ? radarLayer.label :
    activeOverlay === 'pressure' ? 'Lignes de Pression' : 'Risque Orageux';

  return (
    <div
      className="rounded-xl p-4"
      style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
    >
      <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--text-muted)', letterSpacing: '0.08em' }}>
        {title}
      </p>
      {activeOverlay === 'radar' ? (
        <div className="space-y-2">
          <div
            className="rounded-lg p-2"
            style={{ backgroundColor: 'color-mix(in srgb, var(--card) 82%, transparent)', border: '1px solid var(--border)' }}
          >
            <img
              src={radarLegendUrl}
              alt={`Legende ${radarLayer.label}`}
              className="max-w-full h-auto"
              style={{ imageRendering: 'auto' }}
            />
          </div>
          <p className="text-xs leading-snug" style={{ color: 'var(--text-muted)' }}>
            Source cartographique officielle ECCC / MSC GeoMet. Si la carte reste transparente, il peut simplement ne pas y avoir de precipitation sur la zone.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={`legend-${item.label}`} className="flex items-center gap-2.5">
              <span
                className="w-4 h-4 rounded flex-shrink-0"
                style={{ backgroundColor: item.color, border: '1px solid rgba(255,255,255,0.1)' }}
              />
              <div>
                <p className="font-mono-data text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Marker legend */}
      <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
        <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)', letterSpacing: '0.08em' }}>
          Terrains
        </p>
        <div className="space-y-1.5">
          {[
            { color: '#34d399', label: 'Favorable' },
            { color: '#fbbf24', label: 'Marginal' },
            { color: '#f87171', label: 'Non favorable' },
            { color: '#a78bfa', label: 'Risque orage' },
          ].map((m) => (
            <div key={`marker-${m.label}`} className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: m.color }}
              />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{m.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
